import { useTranslation } from 'react-i18next';

import { HumanResourcesItem } from '@/app/models/detailsRH';
import { useUpdateRatioHumanResourcesMutation } from '@/app/services/detailsHRApi';
import DrawerButtons from '@/components/common/DrawerButtons';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import useFormattedCpfAndCpnj from '@/hooks/useFormattedCpfAndCpnj';
import { Box, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { toast } from 'react-toastify';
import FormFieldQuantity from '../baseForms/FormFieldQuantity';

interface NewEditRatioProps {
  selectItem: HumanResourcesItem | null;
  project: string;
  setDrawerOpen: (open: boolean) => void;
}

export default function NewEditRatioHumanResources(props: NewEditRatioProps) {
  const theme = useTheme();
  const { t } = useTranslation(['humanResource', 'common']);

  const { selectItem, setDrawerOpen, project } = props;
  const [ratio, setRatio] = useState<number | null>(selectItem?.ratio || null);

  const { value: cpf } = useFormattedCpfAndCpnj(selectItem?.cpf);
  const [update] = useUpdateRatioHumanResourcesMutation();

  const handleSubmit = async () => {
    setDrawerOpen(false);
    await toast.promise(
      update({
        id: selectItem?.id as number,
        ratio: ratio || 0,
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('hr.updated.successfully') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
  };

  const handleCancel = () => {
    setDrawerOpen(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 'inherit',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle title={t('edit.ratio')} />

        <Box
          sx={{
            display: 'block',
            flexDirection: 'row',
            mt: 2,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'block', minWidth: theme.spacing(31), mb: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="fieldHeader" sx={{ width: theme.spacing(12), mb: 0 }}>
                  {t('common:collaborator')}
                </Typography>
                <TextField
                  sx={{
                    flexGrow: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                  size="small"
                  value={selectItem?.name}
                  disabled
                />
              </Stack>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="fieldHeader" sx={{ width: theme.spacing(12), mb: 0 }}>
                  {t('cpf')}
                </Typography>
                <TextField
                  sx={{
                    flexGrow: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                  size="small"
                  value={cpf}
                  disabled
                />
              </Stack>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="fieldHeader" sx={{ width: theme.spacing(12), mb: 0 }}>
                  {t('origin.project')}
                </Typography>
                <TextField
                  sx={{
                    flexGrow: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                  size="small"
                  value={project}
                  disabled
                />
              </Stack>
            </Box>
          </Box>
        </Box>
        <Box sx={{ width: '100%', height: theme.spacing(0.25), bgcolor: 'grey.300', mt: 2 }} />
        <FormFieldQuantity
          label={t('percent')}
          value={ratio}
          onChange={(e) => {
            if ((e && e <= 100 && e > -1) || e == null || e === undefined) {
              setRatio(e);
            }
          }}
        />
      </Box>
      <Box sx={{ mt: 'auto' }}>
        <DrawerButtons onSave={handleSubmit} onCancel={handleCancel} disabled={false} />
      </Box>
    </Box>
  );
}
